import type {
  AttrData,
  AttrDataPrimitive,
  CamlOptions,
  OptCssNames,
} from 'micromark-extension-caml';
import type {
  AttrBoxNode,
  AttrBoxDataNode,
  AttrBoxListNode,
  AttrKeyNode,
  AttrValNode,
} from '../util/types';

import { defu } from 'defu';
import * as Uni from 'unist';
import { selectAll } from 'unist-util-select';
import * as CAML from 'caml-mkdn';

// required options
interface ReqOpts {
  attrs: {
    render: boolean;
  };
  cssNames: OptCssNames;
}

export function initAttrBox(tree: Uni.Node, opts?: Partial<CamlOptions>): AttrBoxNode | undefined {
  // opts
  const defaults: ReqOpts = {
    attrs: {
      render: true,
    },
    cssNames: {
      attr: 'attr',
      attrbox: 'attrbox',
      attrItem: 'attr-item',
      key: 'key__',
      // [[wikiattrs]]-related
      wiki: 'wiki',
      invalid: 'invalid',
      // types
      reftype: 'reftype__',
      doctype: 'doctype__',
    },
  };
  // defu(opts, defaults): user opts win, defaults fill gaps — parity with wikirefs
  const fullOpts: ReqOpts = defu(opts, defaults) as ReqOpts;

  // todo: should i be making node assertions...?
  //       https://github.com/syntax-tree/unist-util-is#isnode-test-index-parent-context
  // extract attr data nodes
  const attrDataNodes: AttrBoxDataNode[] = selectAll('attrbox-data', tree) as AttrBoxDataNode[];
  // extract attr data
  const attrData: any = attrDataNodes.reduce((acc: any, node: AttrBoxDataNode) => {
    Object.entries(node.data.items).forEach(([key, value]) => {
      if (acc[key]) {
        acc[key] = acc[key].concat(value);
      } else {
        acc[key] = value;
      }
    });
    return acc;
  }, {});
  // build attrbox and return
  return (Object.keys(attrData).length > 0)
    ? buildAttrBox(attrData, fullOpts)
    : undefined;
}

// html properties are meant to build an element like this:
// 
// <aside class='attrbox'>
//    <dl>
//      <dt>key</dt>
//        <dd><span class='attr type key'>val a</span></dd>
//        <dd><span class='attr type key'>val b</span></dd>
//        <dd><span class='attr type key'>val c</span></dd>
//        ...
//    </dl>
// </aside>

export function buildAttrBox(
  attrData: any,
  fullOpts: ReqOpts,
): AttrBoxNode | undefined {
  // init
  const attrbox: AttrBoxNode = {
    type: 'attrbox',
    children: [] as AttrBoxListNode[],
    data: {
      items: {} as AttrData,
      hName: 'aside',
      hProperties: {
        className: [ fullOpts.cssNames.attrbox ],
      },
    },
  };
  const attrBoxListNode: AttrBoxListNode = {
    type: 'attrbox-list',
    children: [] as (AttrKeyNode | AttrValNode)[],
    data: { hName: 'dl' },
  };
  // construct
  attrbox.children.push(attrBoxListNode);
  // if we have attr items, process them
  if (Object.keys(attrData).length > 0) {
    // copy item data
    attrbox.data.items = { ...attrData };
    // build item hProperties from item data
    for (const [attrtype, items] of Object.entries(attrData)) {
      // create a group node (<div>) to wrap each key + its values
      const groupNode: any = {
        type: 'attr-group',
        children: [] as (AttrKeyNode | AttrValNode)[],
        data: { hName: 'div', hProperties: { className: [ fullOpts.cssNames.attrItem ] } },
      };
      attrBoxListNode.children.push(groupNode);
      addAttrKey(groupNode, attrtype, fullOpts);
      for (const item of items as unknown as any) {
        if (item.type === 'wiki') {
          // caml does NOT resolve wikirefs. A wiki value renders like any other value —
          // a plain string span showing the literal [[fname]] (type class 'wiki'). A
          // co-registered remark-wikirefs finds these `attr wiki` spans and upgrades them
          // to resolved links in a later tree transformer (the hand-off — see
          // caml-wikiref-handoff). With no wikirefs, the string span is the output.
          const fname: string = item.filename !== undefined ? item.filename : String(item.value);
          addWikiAttrString(groupNode, attrtype, fname, fullOpts);
        } else {
          // primitive values (from caml-mkdn resolve)
          addAttrVal(groupNode, attrtype, item, fullOpts);
        }
      }
    }
  }

  return attrbox;
}

export function addAttrKey(
  parent: AttrBoxNode | any,
  key: string | undefined,
  fullOpts: ReqOpts,
): void {
  const keyTxt: string | undefined = key ? key.trim() : undefined;
  // the key's class rides the dt: the cssNames.key prefix + the slug. this
  // parser owns its class assembly (mirroring mdast-util-wikirefs' cssNames.reftype
  // dt); slugify is the one shared primitive from caml-mkdn. See caml-spec.
  const keyNode: AttrKeyNode = {
    type: 'attr-key',
    children: [{
      type: 'text',
      value: keyTxt ? keyTxt : 'key error',
    }],
    data: {
      hName: 'dt',
      hProperties: keyTxt ? { className: [fullOpts.cssNames.key + CAML.slugify(keyTxt)] } : undefined,
    },
  };
  // add to parent (attr-group div or attrbox-list)
  parent.children.push(keyNode);
}

export function addAttrVal(
  parent: AttrBoxNode | any,
  key: string | undefined,
  val: AttrDataPrimitive,
  fullOpts: ReqOpts,
): void {
  // For multi-line strings, display the resolved value (folded/literal processed)
  // For all other types, display the raw string
  const displayText: string = (val.string && val.string.includes('\n'))
    ? String(val.value)
    : val.string;
  // Build hast children for multi-line values: use <br> elements for newlines
  // and preserve leading whitespace with &nbsp; so rehype doesn't collapse it
  let hChildren: any[] | undefined;
  if (displayText.includes('\n')) {
    hChildren = [];
    const lines = displayText.split('\n');
    for (let i = 0; i < lines.length; i++) {
      if (i > 0) {
        hChildren.push({ type: 'element', tagName: 'br', properties: {}, children: [] });
      }
      if (lines[i].length > 0) {
        // replace leading spaces with &nbsp; to prevent HTML whitespace collapse
        const preserved = lines[i].replace(/^ +/, (spaces: string) =>
          Array(spaces.length).fill('\u00a0').join('')
        );
        hChildren.push({ type: 'text', value: preserved });
      }
    }
  }
  const valNode: AttrValNode = {
    type: 'attr-val',
    children: [
      {
        type: val.type,
        children: [{ type: 'text', value: displayText }],
        data: {
          hName: 'span',
          hProperties: {
            // structure + value type only -- the raw key's class lives on the dt
            className: [
              fullOpts.cssNames.attr,
              val.type ? val.type.trim().toLowerCase().replace(/ /g, '-').replace(/[^\w-]+/g, '') : 'valtype-error',
            ],
          },
          ...(hChildren ? { hChildren } : {}),
        },
      },
    ],
    data: { hName: 'dd' },
  };
  // add to parent (attr-group div or attrbox-list)
  parent.children.push(valNode);
}

// caml renders a wiki attr value as a plain string span (the 'string' type class)
// showing the literal [[fname]] — it does NOT resolve wikirefs. A co-registered
// remark-wikirefs finds attr spans whose text is [[...]] and upgrades them to resolved
// links in a later tree transformer (the hand-off — see caml-wikiref-handoff).
export function addWikiAttrString(
  parent: AttrBoxNode | any,
  attrtype: string | undefined,
  filename: string,
  fullOpts: any,
): void {
  const cssNames: any = fullOpts.cssNames;
  // structure + type only -- the key's class lives on the dt (key__<slug>)
  const wikiNode: any = {
    type: 'wikiattr',
    children: [{ type: 'text', value: '[[' + filename + ']]' }],
    data: {
      hName: 'span',
      hProperties: {
        className: [
          cssNames.attr,
          'string',
        ],
      },
    },
  };
  parent.children.push({
    type: 'attr-val',
    children: [wikiNode],
    data: { hName: 'dd' },
  } as AttrValNode);
}